import { Component, OnInit, Input } from '@angular/core';
import { ChangeDetectorRef } from '@angular/core';
import { StockFilterService } from 'src/app/features/stock-screener/services/stock-filter.service';
import { ApiResponse } from 'src/app/models/sector-industries/sector-industries.model';

interface DocumentType {
   label: string;
   year: number;
   quarter: number; // Quarter of the record (1, 2, 3, or 4)
   value: number;
}

@Component({
    selector: 'app-income-statement',
    templateUrl: './income-statement.component.html',
    styleUrls: ['./income-statement.component.scss'],
    standalone: false
})
export class IncomeStatementComponent implements OnInit {
   setLoadingState: boolean = true;
   _isAnnual: boolean = true;
   period: number[] = [2020, 2021, 2022, 2023, 2024];
   page_num: number = 1;
   annualData: any[] = [];
   quarterlyData: any[] = [];

   @Input() chartKey: string = '';

   constructor(
      private financialService: StockFilterService,
      private cd: ChangeDetectorRef
   ) {}

   ngOnInit(): void {
      this.isAnnual = true;
      this.setLoadingState = true;
   }

   // FIXED: Removed duplicate changePeriod method and kept only this one
   changePeriod(periodValue: boolean): void {
      // console.log("Period changed:", periodValue);

      // Only update if the value actually changed to prevent duplicate fetches
      if (this._isAnnual !== periodValue) {
         this.isAnnual = periodValue; // This will trigger the setter
      }
   }

   get isAnnual(): boolean {
      return this._isAnnual;
   }

   set isAnnual(value: boolean) {
      this._isAnnual = value;

      // Clear existing data before fetching new data
      if (value) {
         // Clear quarterly data when switching to annual
         this.quarterlyData = [];
         this.getAnnualData();
      } else {
         // Clear annual data when switching to quarterly
         this.annualData = [];
         this.getQuarterlyData();
      }

      // Force change detection after data update
      this.cd.detectChanges();
   }

   getAnnualData() {
      this.setLoadingState = true;
      //this.getActualAnnualData(this.chartKey,"annual",this.period,this.page_num,this.annualData)
      this.getNewActualAnnualData(
         this.chartKey,
         'annual',
         this.page_num,
         this.annualData
      );
   }

   getQuarterlyData() {
      this.setLoadingState = true;
      // console.log('Fetching quarterly data...');
      this.getNewActualQuarterlyData(
         this.chartKey,
         'quarterly',
         this.page_num,
         this.quarterlyData
      );
   }

   async getNewActualAnnualData(
      ticker: string,
      freq: string,
      page_num: number,
      targetArray: any[]
   ) {
      try {
         // Define the priority labels in the desired order
         const topLabels = [
            'revenue',
            'costOfGoodsSold',
            'grossIncome',
            'totalOperatingExpense',
            'researchDevelopment',
            'sgaExpense',
            'ebit',
            'totalOtherIncomeExpenseNet',
            'interestIncomeExpense',
            'pretaxIncome',
            'provisionforIncomeTaxes',
            'netIncomeAfterTaxes',
            'netIncome',
            'dilutedAverageSharesOutstanding',
            'dilutedEPS',
         ];

         let allData: any[] = [];
         let hasMoreData = true;

         // Fetch data until there are no more pages (assuming less than 100 hits means last page)
         while (hasMoreData) {
            const response = await this.financialService.getNewIncomeData(
               ticker,
               freq,
               page_num
            );
            // console.log('Response is', response);

            if (response && response.hits && response.hits.length > 0) {
               allData.push(...response.hits);
               if (response.hits.length < 100) {
                  hasMoreData = false;
               } else {
                  page_num++;
               }
            } else {
               hasMoreData = false;
            }
         }

         // Build a map where the key is the label (only for topLabels) and the value is an object mapping year to value.
         const labelMap = new Map<string, Record<string, number>>();

         // Process each document/hit
         allData.forEach((hit: any) => {
            const doc = hit.document;
            const year = doc.year;
            if (!year) return; // Skip if no year is provided

            // Only process the labels specified in topLabels
            topLabels.forEach((label) => {
               if (
                  doc[label] !== undefined &&
                  doc[label] !== '' &&
                  doc[label] !== null
               ) {
                  const value = parseFloat(doc[label]);
                  if (!isNaN(value)) {
                     if (!labelMap.has(label)) {
                        labelMap.set(label, { [year]: value });
                     } else {
                        const existingData = labelMap.get(label)!;
                        existingData[year] = value;
                        labelMap.set(label, existingData);
                     }
                  }
               }
            });
         });

         // Convert the Map into an array, preserving the order defined in topLabels
         const result = Array.from(labelMap.entries())
            .map(([label, data]) => ({ label, data }))
            .sort(
               (a, b) => topLabels.indexOf(a.label) - topLabels.indexOf(b.label)
            );

         // Filter out any entries that have no data (just in case)
         const filteredResult = result.filter(
            (item) => item.data && Object.keys(item.data).length > 0
         );

         this.setLoadingState = false;
         targetArray.push(...filteredResult);
         // console.log('target array is ', targetArray);

         return filteredResult;
      } catch (error) {
         this.setLoadingState = false;
         throw error;
      }
   }

   async getNewActualQuarterlyData(
      ticker: string,
      freq: string,
      page_num: number,
      targetArray: any[]
   ) {
      try {
         let allData: any[] = [];
         let hasMoreData = true;

         while (hasMoreData) {
            const response = await this.financialService.getNewIncomeData(
               ticker,
               freq,
               page_num
            );
            // console.log('Response 2 is', response);

            if (response?.hits?.length > 0) {
               allData.push(...response.hits);
               hasMoreData = response.hits.length >= 100;
               if (hasMoreData) page_num++;
            } else {
               hasMoreData = false;
            }
         }

         const labelMap = new Map<string, Record<string, number>>();
         const topLabels = [
            'revenue',
            'costOfGoodsSold',
            'grossIncome',
            'totalOperatingExpense',
            'researchDevelopment',
            'sgaExpense',
            'ebit',
            'totalOtherIncomeExpenseNet',
            'interestIncomeExpense',
            'pretaxIncome',
            'provisionforIncomeTaxes',
            'netIncomeAfterTaxes',
            'netIncome',
            'dilutedAverageSharesOutstanding',
            'dilutedEPS',
         ];

         allData.forEach((hit: any) => {
            const doc = hit.document;
            if (!doc?.period) return; // Skip if period is missing
            if (/^\d{4}$/.test(String(doc.period))) return; // Skip annual records

            const quarterLabel = this.formatQuarterLabel(doc.period);
            if (!quarterLabel) return;

            topLabels.forEach((label) => {
               const value = parseFloat(doc[label]);
               if (!isNaN(value)) {
                  if (!labelMap.has(label)) {
                     labelMap.set(label, { [quarterLabel]: value });
                  } else {
                     labelMap.get(label)![quarterLabel] = value;
                  }
               }
            });
         });

         const result = Array.from(labelMap.entries()).map(([label, data]) => ({
            label,
            data,
         }));

         this.setLoadingState = false;

         // Directly updating the reference instead of modifying the array
         this.quarterlyData = [...result];
         // console.log('Updated quarterlyData:', this.quarterlyData);

         // Manually trigger change detection
         this.cd.detectChanges();
      } catch (error) {
         this.setLoadingState = false;
         // console.error('Error fetching quarterly data:', error);
      }
   }

   formatQuarterLabel(dateString: string): string | null {
      if (!dateString) return null;

      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
         // console.error('Invalid date:', dateString);
         return null;
      }

      const month = date.getMonth() + 1;
      const year = date.getFullYear().toString().slice(-2);
      const quarter = Math.ceil(month / 3);

      return `Q${quarter}'${year}`;
   }

   transformData(arrays: any[][]): any[] {
      const resultMap = new Map<string, any>();

      for (const array of arrays) {
         for (const doc of array) {
            const label = doc.label;
            const year = doc.year;
            const value = doc.value;

            if (!resultMap.has(label)) {
               resultMap.set(label, { label });
            }

            resultMap.get(label)[year] = value;
         }
      }

      return Array.from(resultMap.values());
   }
}
